import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { startOfDay, endOfDay, isSameDay } from "date-fns";
import {
  formatDate,
  formatDateTimeWIthTimezone,
} from "../../../utils/dateTimeHelper";
import { Button } from "../Button";
import localization from "../../../config/localization";
import { useLanguage } from "../../../config/localization/LanguageProvider";

interface DateRangePickerProps {
  id?: string;
  label?: string;
  placeholder?: string;
  startDate?: string | null;
  endDate?: string | null;
  onRangeChange: (start: string, end: string) => void;
  error?: string;
  isRequired?: boolean;
  variant?: "default" | "auth";
  disableFutureDates?: boolean;
  disabled?: boolean;
  onApply?: (start: string, end: string) => void;
  onClear?: () => void;
}

export function DateRangePicker({
  id,
  label,
  placeholder,
  startDate,
  endDate,
  onRangeChange,
  error,
  isRequired,
  variant = "default",
  disableFutureDates = false,
  disabled = false,
  onApply,
  onClear,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [openUpward, setOpenUpward] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [rangeStart, setRangeStart] = useState<Date | null>(
    startDate ? new Date(startDate) : null,
  );
  const [rangeEnd, setRangeEnd] = useState<Date | null>(
    endDate ? new Date(endDate) : null,
  );
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const monthDropdownRef = useRef<HTMLDivElement>(null);

  const { language } = useLanguage();

  useEffect(() => {
    if (!startDate || !endDate) {
      setInputValue("");
      setRangeStart(null);
      setRangeEnd(null);
      setCurrentMonth(new Date());
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

    setInputValue(`${formatDate(start)} \u2192 ${formatDate(end)}`);
    setRangeStart(startOfDay(start));
    setRangeEnd(endOfDay(end));
    setCurrentMonth(start);
  }, [startDate, endDate]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      const clickedOutsideCalendar =
        modalRef.current && !modalRef.current.contains(target);

      const clickedOutsideMonthDropdown =
        showMonthDropdown &&
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(target);

      const clickedOutsideYearDropdown =
        showYearDropdown &&
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(target);

      if (clickedOutsideCalendar) {
        setIsOpen(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
        return;
      }

      if (clickedOutsideMonthDropdown) {
        setShowMonthDropdown(false);
      }

      if (clickedOutsideYearDropdown) {
        setShowYearDropdown(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, showMonthDropdown, showYearDropdown]);

  useEffect(() => {
    if (showYearDropdown && yearDropdownRef.current) {
      const currentYear = currentMonth.getFullYear();
      const selectedYearButton = yearDropdownRef.current.querySelector(
        `button[data-year="${currentYear}"]`,
      );
      if (selectedYearButton) {
        selectedYearButton.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
  }, [showYearDropdown, currentMonth]);

  useEffect(() => {
    if (showMonthDropdown && monthDropdownRef.current) {
      const currentMonthIndex = currentMonth.getMonth();
      const selectedMonthButton = monthDropdownRef.current.querySelector(
        `button[data-month="${currentMonthIndex}"]`,
      );
      if (selectedMonthButton) {
        selectedMonthButton.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
  }, [showMonthDropdown, currentMonth]);

  const handleInputClick = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const calendarHeight = 600;

      setOpenUpward(spaceBelow < calendarHeight && spaceAbove > spaceBelow);
    }
    setIsOpen(!isOpen);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const handlePrevMonth = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1),
    );
  };

  const handleNextMonth = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
    );
  };

  const handleDateSelect = (day: number) => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(startOfDay(selectedDate));
      setRangeEnd(null);
    } else {
      if (selectedDate < rangeStart) {
        setRangeEnd(endOfDay(rangeStart));
        setRangeStart(startOfDay(selectedDate));
      } else {
        setRangeEnd(endOfDay(selectedDate));
      }
    }
  };

  const handleClear = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    setRangeStart(null);
    setRangeEnd(null);
    onRangeChange("", "");
    setInputValue("");
    setCurrentMonth(new Date());
    setIsOpen(false);
    if (onClear && rangeStart && rangeEnd) {
      onClear();
    }
  };

  const handleSubmit = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    if (rangeStart && rangeEnd) {
      onRangeChange(
        formatDateTimeWIthTimezone(rangeStart),
        formatDateTimeWIthTimezone(rangeEnd),
      );
      setIsOpen(false);
    }
    if (onApply && rangeStart && rangeEnd) {
      onApply(
        formatDateTimeWIthTimezone(rangeStart),
        formatDateTimeWIthTimezone(rangeEnd),
      );
    }
  };

  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth()));
    setShowYearDropdown(false);
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex));
    setShowMonthDropdown(false);
  };

  const generateYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear + 10; i >= 1950; i--) {
      years.push(i);
    }
    return years;
  };

  const generateMonthRange = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push(
        new Date(currentMonth.getFullYear(), i, 1).toLocaleString(language, {
          month: "long",
        }),
      );
    }
    return months;
  };

  const formatter = new Intl.DateTimeFormat(language, { weekday: "short" });
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map((day) => {
    const date = new Date(2024, 0, 7 + day);
    return formatter.format(date);
  });

  const isInRange = (day: number | null) => {
    if (!day) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    if (rangeStart && rangeEnd) {
      return (
        formatDate(date) > formatDate(rangeStart) &&
        formatDate(date) < formatDate(rangeEnd)
      );
    }

    if (rangeStart && hoverDate && !rangeEnd) {
      const start = rangeStart < hoverDate ? rangeStart : hoverDate;
      const end = rangeStart < hoverDate ? hoverDate : rangeStart;
      return date > start && date < end;
    }

    return false;
  };

  const isRangeStart = (day: number | null) => {
    if (!day || !rangeStart) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    return isSameDay(date, rangeStart);
  };

  const isRangeEnd = (day: number | null) => {
    if (!day || !rangeEnd) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    return isSameDay(date, rangeEnd);
  };

  const isToday = (day: number | null) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString(language, {
    month: "long",
  });
  const yearValue = currentMonth.getFullYear();

  const isSingleDayRange = (day: number | null) => {
    if (!day || !rangeStart || !rangeEnd) return false;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    return isSameDay(rangeStart, rangeEnd) && isSameDay(date, rangeStart);
  };

  const handleClearStart = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setHoverDate(null);
  };

  const handleClearEnd = () => {
    setRangeEnd(null);
    setHoverDate(null);
  };

  const isFutureDate = (day: number | null) => {
    if (!day || !disableFutureDates) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  return (
    <div className="mb-2" id={id}>
      {label && (
        <label className="block text-[#A9A9A9] mb-1 ml-1 font-medium">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative" ref={modalRef}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          readOnly
          placeholder={placeholder}
          onClick={handleInputClick}
          disabled={disabled}
          className={`w-full px-4 py-2 h-12 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#333333] cursor-pointer
            ${variant === "default" ? "bg-[#000000]" : "bg-[#1D1D1D]"}
            border ${error ? "border-red-500" : "border-[#333333]"}
            pr-12
          `}
        />

        <Button
          type="button"
          onClick={handleInputClick}
          disabled={disabled}
          id="calendar-icon-button"
          className={`absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white ${disabled ? "cursor-not-allowed" : "cursor-pointer"} shadow-none`}
        >
          <img
            src={"/icons/ic-calendar.svg"}
            alt="calendar"
            className="size-6"
          />
        </Button>

        {isOpen && (
          <div
            className={`absolute right-0 bg-white rounded-lg shadow-2xl p-3 z-20 w-80 max-h-[600px]${openUpward ? "bottom-full mb-2" : "top-full mt-2"
              }`}
          >
            <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-[#EDEDED] rounded-lg">
              <div className="flex items-center justify-between bg-white rounded-lg p-2">
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="text-md font-medium text-black">
                      {localization.start}
                    </div>
                  </div>

                  <div
                    className={`text-md font-semibold ${rangeStart ? "text-[#06876D]" : "text-gray-500"}`}
                  >
                    {rangeStart ? formatDate(rangeStart) : "Please Select"}
                  </div>
                </div>

                <div className="flex items-center ">
                  {rangeStart && (
                    <Button
                      type="button"
                      onClick={handleClearStart}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors shadow-none"
                      aria-label="Clear start date"
                    >
                      <FontAwesomeIcon icon={faTimes} size="2xs" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between bg-white rounded-lg p-2">
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="text-md font-medium text-black mb-1">
                      {localization.end}
                    </div>
                  </div>

                  <div
                    className={`text-md font-semibold ${rangeEnd ? "text-[#06876D]" : "text-gray-500"}`}
                  >
                    {rangeEnd ? formatDate(rangeEnd) : "Please Select"}
                  </div>
                </div>

                <div className="flex items-center">
                  {rangeEnd && (
                    <Button
                      type="button"
                      onClick={handleClearEnd}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-300 text-gray-700 hover:bg-gray-400 transition-colors shadow-none"
                      aria-label="Clear end date"
                    >
                      <FontAwesomeIcon icon={faTimes} size="2xs" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-4 p-2">
              <Button
                type="button"
                onClick={handlePrevMonth}
                className="w-8 h-8 flex items-center justify-center text-[#9C9C9C] hover:bg-gray-100 rounded-full shadow-none"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <div className="relative">
                <h3 className="text-lg font-semibold text-[#CF7A25] flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                    className="hover:bg-gray-100 px-2 py-1 rounded transition-colors shadow-none"
                  >
                    {monthName}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowYearDropdown(!showYearDropdown)}
                    className="hover:bg-gray-100 px-2 py-1 rounded transition-colors shadow-none"
                  >
                    {yearValue}
                  </Button>
                </h3>

                {showMonthDropdown && (
                  <div
                    ref={monthDropdownRef}
                    className="absolute top-full right-0 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-30 w-30 max-h-48 overflow-y-auto"
                  >
                    {generateMonthRange().map((monthName, index) => (
                      <Button
                        key={index}
                        type="button"
                        data-month={index}
                        onClick={() => handleMonthSelect(index)}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${index === currentMonth.getMonth()
                          ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                          : "text-gray-900"
                          } shadow-none`}
                      >
                        {monthName}
                      </Button>
                    ))}
                  </div>
                )}

                {showYearDropdown && (
                  <div
                    ref={yearDropdownRef}
                    className="absolute top-full right-0 mt-2 bg-white border-2 border-gray-300 rounded-lg shadow-xl z-50 w-32 max-h-48 overflow-y-auto"
                  >
                    {generateYearRange().map((year) => (
                      <Button
                        key={year}
                        type="button"
                        data-year={year}
                        onClick={() => handleYearSelect(year)}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${year === yearValue
                          ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                          : "text-gray-900"
                          } shadow-none`}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="button"
                onClick={handleNextMonth}
                className="w-8 h-8 flex items-center justify-center text-[#9C9C9C] hover:bg-gray-100 rounded-full shadow-none"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-bold text-[#4A4A4A] py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {days.map((day, index) => (
                <div key={index} className="aspect-square relative">
                  {day ? (
                    <div
                      className="relative w-full h-full"
                      onMouseEnter={() => {
                        if (rangeStart && !rangeEnd) {
                          setHoverDate(
                            new Date(
                              currentMonth.getFullYear(),
                              currentMonth.getMonth(),
                              day,
                            ),
                          );
                        }
                      }}
                      onMouseLeave={() => setHoverDate(null)}
                    >
                      {isInRange(day) && (
                        <div className="absolute inset-0 bg-[#660027]"></div>
                      )}

                      {isRangeStart(day) &&
                        rangeEnd &&
                        !isSingleDayRange(day) && (
                          <div className="absolute top-0 right-0 bottom-0 w-1/2 bg-[#660027]"></div>
                        )}

                      {isRangeEnd(day) &&
                        rangeStart &&
                        !isSingleDayRange(day) && (
                          <div className="absolute top-0 left-0 bottom-0 w-1/2 bg-[#660027]"></div>
                        )}

                      <Button
                        type="button"
                        onClick={() => handleDateSelect(day)}
                        disabled={disableFutureDates && isFutureDate(day)}
                        className={`relative w-full h-full flex items-center justify-center rounded-full text-sm font-medium transition-colors z-10
                          ${disableFutureDates && isFutureDate(day)
                            ? "text-gray-300 cursor-not-allowed"
                            : isRangeStart(day) || isRangeEnd(day)
                              ? "bg-[#FD3A84] text-white hover:bg-[#e5356e]"
                              : isInRange(day)
                                ? "text-white"
                                : "text-gray-900 hover:bg-gray-100"
                          }
                          shadow-none
                        `}
                      >
                        {day}
                      </Button>
                      {isToday(day) &&
                        !isRangeStart(day) &&
                        !isRangeEnd(day) && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#FD3A84] rounded-full z-20"></div>
                        )}
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-4 gap-2">
              <Button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium shadow-none"
              >
                {localization.clear}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!rangeStart || !rangeEnd}
                className={`px-6 py-2 rounded-lg font-medium shadow-none ${rangeStart && rangeEnd
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
              >
                {localization.apply}
              </Button>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-red-500 text-sm mt-1 ml-2">{error}</div>}
    </div>
  );
}
